import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import classNames from 'classnames';
import { ReactElement, useEffect, useId, useRef, useState } from 'react';
import { ReactComponent as IconChevron } from '../../../assets/icons/chevron.svg';
import dropdownStyle from '../dropdown/dropdown.module.scss';
import styles from './multi-select-dropdown.module.scss';

export interface MultiSelectDropdownItem {
  value: string;
  label: string;
  adornment?: React.ReactNode;
}

export interface MultiSelectDropdownProps {
  label: string;
  items: MultiSelectDropdownItem[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  renderValue?: (selected: string[]) => React.ReactNode;
}

export const MultiSelectDropdownComponent = ({
  label,
  items,
  value,
  onChange,
  placeholder,
  disabled = false,
  renderValue,
}: MultiSelectDropdownProps): JSX.Element => {
  const generatedId = useId();
  const id = `multi-select-dropdown-${generatedId}`;
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLElement | null>(null);

  const handleDropdown = (): void => {
    setIsOpen(false);
  };

  useEffect(() => {
    window.addEventListener('scroll', () => handleDropdown());

    return () => {
      window.removeEventListener('scroll', () => handleDropdown());
    };
  }, []);

  const handleChange = (event: SelectChangeEvent<string[]>): void => {
    const selectedValues = event.target.value;
    onChange(typeof selectedValues === 'string' ? selectedValues.split(',') : selectedValues);
  };

  const defaultRenderValue = (selected: string[]): React.ReactNode => {
    if (selected.length === 0) {
      return placeholder ? <span className={dropdownStyle.Placeholder}>{placeholder}</span> : '';
    }

    // eslint-disable-next-line spellcheck/spell-checker
    return `${selected.length} ausgewählt`;
  };

  const hasSelection = value.length > 0;

  return (
    <div
      className={classNames(dropdownStyle.DropdownContainer, 'MultiSelectDropdown', {
        [dropdownStyle.Disabled]: disabled,
      })}
    >
      <FormControl fullWidth>
        {label && (
          <InputLabel id={`${id}-label`} shrink={isOpen || hasSelection}>
            {label}
          </InputLabel>
        )}
        <Select
          id={id}
          multiple
          className={classNames(dropdownStyle.DropdownWidget, {
            [dropdownStyle.ItemSelected]: hasSelection,
            [dropdownStyle.IsOpen]: isOpen,
            [dropdownStyle.HasLabel]: label,
          })}
          value={value}
          fullWidth
          displayEmpty
          disabled={disabled}
          onChange={handleChange}
          IconComponent={(props): ReactElement => <IconChevron {...props} />}
          renderValue={(selected): React.ReactNode =>
            renderValue ? renderValue(selected as string[]) : defaultRenderValue(selected as string[])
          }
          MenuProps={{ className: dropdownStyle.DropdownMenu }}
          onOpen={(): void => setIsOpen(true)}
          onClose={(): void => setIsOpen(false)}
          ref={dropdownRef}
        >
          {items.map((item) => (
            <MenuItem key={item.value} value={item.value} className={dropdownStyle.DropdownMenuItem}>
              <span className={styles.MenuItemContent}>
                {item.adornment}
                {item.label}
              </span>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};
